import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from "framer-motion";
import { useAppSelector } from "../../store/hooks";
import EmojiKeyboard from "../emojis/components/EmojiKeyboard";
import { useClaimEmojiMutation, useGetEmojiSelectorQuery } from "../emojis/api/emojiApi";
import { emojiKey } from "../emojis/components/emojiCatalogShared";
import { useSetBirthdayMutation } from "../../services/rtkApi/statusApi";
import { useFinishWelcomeMutation, useGetWelcomeQuery } from "./api/welcomeApi";
import "./welcome.css";

/**
 * /welcome: what a new member sees right after their first sign-in. The OAuth
 * callback sends them here when GET /api/welcome says `show`.
 *
 *   created   "account created" animation; its check box turns into the emoji slot
 *   emoji     [ box ] . username, tap the box to pick from the registry, then claim
 *   birthday  optional
 *   done      "you're in", then home, still signed in
 *
 * It keeps the signup page's look (light blue, JetBrains Mono, blue buttons) so
 * the step from the auth pages into the site reads as one flow. It covers the
 * site chrome (z-index 1005, over the mobile nav bar at 1000) but stays under
 * the emoji keyboard's sheet (1010).
 */

type Phase = "created" | "emoji" | "birthday" | "done";

const PAGE_BLUE = "#8be0f5";
const INTRO_MS = 1900;
const DONE_MS = 1200;

/* ------------------------------ typing text ------------------------------ */

interface TypedProps {
  text: string;
  /** ms before the first letter */
  delay?: number;
  /** ms per letter */
  speed?: number;
  cursor?: boolean;
  className?: string;
}

/** Types `text` out letter by letter. Screen readers get the whole text at once. */
const Typed: React.FC<TypedProps> = ({ text, delay = 0, speed = 55, cursor = false, className }) => {
  const reduce = useReducedMotion();
  const [count, setCount] = useState(reduce ? text.length : 0);

  useEffect(() => {
    if (reduce) {
      setCount(text.length);
      return;
    }
    setCount(0);
    let timer: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      timer = setInterval(() => {
        setCount((n) => {
          if (n + 1 >= text.length && timer) clearInterval(timer);
          return Math.min(n + 1, text.length);
        });
      }, speed);
    }, delay);
    return () => {
      clearTimeout(start);
      if (timer) clearInterval(timer);
    };
  }, [text, delay, speed, reduce]);

  return (
    <span className={className} aria-label={text}>
      <span aria-hidden>{text.slice(0, count)}</span>
      {cursor && <span className="wl-cursor" aria-hidden />}
    </span>
  );
};

/* ------------------------------ claim burst ------------------------------ */

const CONFETTI_COLORS = ["#f7dc70", "#ff8cc3", "#66d6f5", "#000000", "#60fac1"];
// fixed, not random, so every claim looks the same and renders are stable
const CONFETTI = Array.from({ length: 10 }, (_, i) => {
  const angle = (i / 10) * Math.PI * 2 + (i % 3) * 0.17;
  const distance = 56 + ((i * 37) % 24);
  return {
    x: Math.cos(angle) * distance,
    // a little gravity: everything ends lower than it flew
    y: Math.sin(angle) * distance + 16,
    rotate: (i % 2 ? 1 : -1) * (90 + ((i * 53) % 120)),
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: (i % 4) * 0.025,
  };
});

const Confetti: React.FC = () => (
  <span className="wl-burst" aria-hidden>
    {CONFETTI.map((c, i) => (
      <motion.span
        key={i}
        className="wl-confetti"
        style={{ backgroundColor: c.color }}
        initial={{ x: 0, y: 0, rotate: 0, scale: 0.3, opacity: 1 }}
        animate={{ x: c.x, y: c.y, rotate: c.rotate, scale: 1, opacity: [1, 1, 0] }}
        transition={{
          default: { duration: 0.9, delay: c.delay, ease: [0.15, 0.8, 0.3, 1] },
          // fully visible for most of the flight, gone at the end
          opacity: { duration: 1, delay: c.delay, times: [0, 0.7, 1], ease: "linear" },
        }}
      />
    ))}
  </span>
);

/* --------------------------------- page ---------------------------------- */

function errorText(err: unknown, fallback: string): string {
  const data = (err as { data?: { error?: string; message?: string } })?.data;
  return data?.error || data?.message || fallback;
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const auth = useAppSelector((s) => s.auth);
  const signedIn = Boolean(auth.token);

  const { data: welcome, isError: welcomeFailed } = useGetWelcomeQuery(undefined, {
    skip: !signedIn,
  });
  // decided once: claiming the emoji turns `show` off, and the page must not
  // walk away from itself halfway through
  const [gate, setGate] = useState<"wait" | "go" | "leave">("wait");
  useEffect(() => {
    if (gate !== "wait") return;
    if (welcome) setGate(welcome.show ? "go" : "leave");
    else if (welcomeFailed) setGate("leave");
  }, [gate, welcome, welcomeFailed]);
  useEffect(() => {
    if (gate === "leave") navigate("/", { replace: true });
  }, [gate, navigate]);

  const username = welcome?.username || auth.username || "";

  const [phase, setPhase] = useState<Phase>("created");
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState("");
  const [birthdayError, setBirthdayError] = useState<string | null>(null);

  const [claimEmoji, { isLoading: claiming }] = useClaimEmojiMutation();
  const [setBirthday, { isLoading: savingBirthday }] = useSetBirthdayMutation();
  const [finishWelcome] = useFinishWelcomeMutation();

  // the registry, for dimming emojis someone else already holds
  const { data: selector } = useGetEmojiSelectorQuery(undefined, { skip: gate !== "go" });
  const unavailable = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of selector?.data?.categories ?? []) {
      for (const e of c.emojis) {
        if (e.mine) continue;
        if (e.taken) map.set(emojiKey(e.e), e.owner ? `${e.owner} already has this one` : "someone already has this one");
        else if (e.reserved) map.set(emojiKey(e.e), "this one is reserved by the site");
      }
    }
    return map;
  }, [selector]);
  const blockedReason = useCallback((emoji: string) => unavailable.get(emojiKey(emoji)) ?? null, [unavailable]);

  /* intro: plays once the page knows it should show */
  useEffect(() => {
    if (gate !== "go" || phase !== "created") return;
    const t = setTimeout(() => setPhase("emoji"), reduce ? 600 : INTRO_MS);
    return () => clearTimeout(t);
  }, [gate, phase, reduce]);

  /* done: mark the screen finished, then home */
  const leaving = useRef(false);
  useEffect(() => {
    if (phase !== "done" || leaving.current) return;
    leaving.current = true;
    const started = Date.now();
    finishWelcome()
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        const wait = Math.max(0, (reduce ? 300 : DONE_MS) - (Date.now() - started));
        setTimeout(() => navigate("/", { replace: true }), wait);
      });
  }, [phase, finishWelcome, navigate, reduce]);

  /* page chrome: lock the page behind, tint the phone's browser bar */
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta?.getAttribute("content") ?? null;
    meta?.setAttribute("content", PAGE_BLUE);
    return () => {
      document.body.style.overflow = prevOverflow;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, []);

  const onPick = (emoji: string) => {
    setPicked(emoji);
    setClaimError(null);
  };

  const onClaim = async () => {
    if (!picked || claiming || claimed) return;
    setClaimError(null);
    try {
      await claimEmoji({ emoji: picked }).unwrap();
      setClaimed(true);
      setTimeout(() => setPhase("birthday"), reduce ? 200 : 800);
    } catch (err) {
      setClaimError(errorText(err, "Could not claim that emoji. Try another one."));
    }
  };

  const onSaveBirthday = async () => {
    if (!birthDate || savingBirthday) return;
    setBirthdayError(null);
    try {
      await setBirthday({ birthDate: new Date(birthDate).toISOString() }).unwrap();
      setPhase("done");
    } catch (err) {
      setBirthdayError(errorText(err, "Could not save your birthday. Try again."));
    }
  };

  const openKeyboard = () => {
    if (phase === "emoji" && !claimed) setKeyboardOpen(true);
  };

  const showHandle = phase !== "created";
  const handleDelay = reduce ? 0 : 0.25;

  const page = (
    <div className="wl-page" role="main">
      {gate === "go" && (
        <LayoutGroup>
          <div className="wl-inner" onClick={phase === "created" ? () => setPhase("emoji") : undefined}>
            {/* ------------------------------ intro ------------------------------ */}
            {phase === "created" && (
              <div className="wl-intro">
                <motion.div
                  layoutId="wl-box"
                  className="wl-stamp"
                  initial={reduce ? false : { scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <svg viewBox="0 0 48 48" width="44" height="44" aria-hidden>
                    <motion.path
                      d="M12 25 L21 34 L37 15"
                      fill="none"
                      stroke="#000"
                      strokeWidth="4.5"
                      strokeLinecap="square"
                      initial={reduce ? false : { pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.2, duration: 0.45, ease: "easeOut" }}
                    />
                  </svg>
                </motion.div>
                <h1 className="wl-title">
                  <Typed text="account created" delay={350} speed={35} cursor />
                </h1>
                <motion.p
                  className="wl-lead"
                  initial={reduce ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: reduce ? 0 : 1, duration: 0.3 }}
                >
                  welcome to n450s. one more thing before you go in.
                </motion.p>
              </div>
            )}

            {/* ------------------------- handle + steps -------------------------- */}
            {showHandle && (
              <div className="wl-steps">
                <AnimatePresence mode="wait">
                  <motion.h1
                    key={phase}
                    className="wl-title"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8, transition: { duration: reduce ? 0 : 0.2 } }}
                    // the first heading waits for the box to land from the intro
                    transition={{ duration: reduce ? 0 : 0.25, delay: reduce || phase !== "emoji" ? 0 : 0.35 }}
                  >
                    {phase === "emoji" && "pick your emoji"}
                    {phase === "birthday" && "when's your birthday?"}
                    {phase === "done" && <Typed text="you're in" speed={40} cursor />}
                  </motion.h1>
                </AnimatePresence>

                <div className="wl-handle-wrap">
                  <motion.div layout className="wl-handle" data-small={phase !== "emoji" ? "" : undefined}>
                    <motion.button
                      type="button"
                      layoutId="wl-box"
                      className="wl-box"
                      data-empty={picked ? undefined : ""}
                      data-claimed={claimed ? "" : undefined}
                      onClick={openKeyboard}
                      disabled={phase !== "emoji" || claimed}
                      aria-label={picked ? `your emoji: ${picked}. tap to change` : "pick your emoji"}
                      animate={claimed && !reduce ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                      transition={claimed ? { duration: 0.35, times: [0, 0.4, 1] } : { type: "spring", stiffness: 300, damping: 26 }}
                    >
                      <AnimatePresence mode="popLayout" initial={false}>
                        {picked ? (
                          <motion.span
                            key={picked}
                            className="wl-box-emoji"
                            initial={reduce ? false : { scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 400, damping: 28 }}
                          >
                            {picked}
                          </motion.span>
                        ) : (
                          <motion.span key="plus" className="wl-box-plus" exit={{ opacity: 0 }}>
                            +
                          </motion.span>
                        )}
                      </AnimatePresence>
                      {claimed && !reduce && <Confetti />}
                    </motion.button>
                    <motion.span
                      layout="position"
                      className="wl-dot"
                      initial={reduce ? false : { scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: handleDelay, duration: 0.2 }}
                    >
                      .
                    </motion.span>
                    <motion.span layout="position" className="wl-username">
                      <Typed text={username} delay={reduce ? 0 : 350} speed={35} cursor={phase === "emoji" && !picked} />
                    </motion.span>
                  </motion.div>

                  <EmojiKeyboard
                    open={keyboardOpen}
                    onClose={() => setKeyboardOpen(false)}
                    onPick={onPick}
                    mode="pick"
                    blockedReason={blockedReason}
                    selected={picked ? [picked] : []}
                    title="Pick your emoji"
                    preload
                  />
                </div>

                <AnimatePresence mode="wait">
                  {phase === "emoji" && (
                    <motion.div
                      key="emoji"
                      className="wl-panel"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6, transition: { duration: reduce ? 0 : 0.2 } }}
                      transition={{ delay: reduce ? 0 : 0.9, duration: 0.3 }}
                    >
                      <p className="wl-hint">
                        {picked
                          ? "this is how you'll show up everywhere on the site. one emoji per person, and it's yours for good."
                          : "it goes in the box, before your name, everywhere on the site. tap the box to choose."}
                      </p>
                      {claimError && (
                        <p className="wl-error" role="alert">
                          {claimError}
                        </p>
                      )}
                      {picked ? (
                        <>
                          <button type="button" className="wl-button" onClick={onClaim} disabled={claiming || claimed}>
                            {claimed ? "it's yours" : claiming ? "claiming…" : `claim ${picked}`}
                          </button>
                          {!claimed && (
                            <button
                              type="button"
                              className="wl-link"
                              onClick={() => setKeyboardOpen(true)}
                              disabled={claiming}
                            >
                              pick another
                            </button>
                          )}
                        </>
                      ) : (
                        <button type="button" className="wl-button" onClick={() => setKeyboardOpen(true)}>
                          pick an emoji
                        </button>
                      )}
                    </motion.div>
                  )}

                  {phase === "birthday" && (
                    <motion.form
                      key="birthday"
                      className="wl-panel"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6, transition: { duration: reduce ? 0 : 0.2 } }}
                      transition={{ duration: reduce ? 0 : 0.3 }}
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        onSaveBirthday();
                      }}
                    >
                      <p className="wl-hint">
                        optional. on your birthday you get perks and a head start on the Art of the Day.
                      </p>
                      <label htmlFor="wl-birthday" className="wl-label">
                        birthday
                      </label>
                      <input
                        id="wl-birthday"
                        type="date"
                        className="wl-input"
                        value={birthDate}
                        min="1900-01-01"
                        max={todayIso()}
                        onChange={(ev) => {
                          setBirthDate(ev.target.value);
                          setBirthdayError(null);
                        }}
                      />
                      {birthdayError && (
                        <p className="wl-error" role="alert">
                          {birthdayError}
                        </p>
                      )}
                      <button type="submit" className="wl-button" disabled={!birthDate || savingBirthday}>
                        {savingBirthday ? "saving…" : "save"}
                      </button>
                      <button
                        type="button"
                        className="wl-link"
                        onClick={() => setPhase("done")}
                        disabled={savingBirthday}
                      >
                        skip
                      </button>
                    </motion.form>
                  )}

                  {phase === "done" && (
                    <motion.p
                      key="done"
                      className="wl-hint"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: reduce ? 0 : 0.5, duration: 0.3 }}
                    >
                      taking you home…
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </LayoutGroup>
      )}
    </div>
  );

  return createPortal(page, document.body);
};

export default WelcomePage;
